import React from "react";
import { Menu } from "react-feather";
import { ChevronDown } from "../../SVG/ChevronDown";
import { IconActionMenuTrigger } from "../icon-action-menu/icon-action-menu-trigger";
import type { IconActionMenuItem } from "../icon-action-menu/icon-action-menu";
import "./section-header-row.scss";

export interface SectionHeaderRowProps {
  /** Step or section number shown in the blue badge. Omit or pass `undefined` to hide the badge. */
  stepNumber?: number;
  /** Main title (e.g. "Medication/Prescription checks"). */
  title: string;
  /** Optional description below the title. */
  description?: string;
  /** Menu items for the gear (settings) IconActionMenuTrigger. */
  actionMenuItems: IconActionMenuItem[];
  /** Whether the section is expanded (chevron rotation). */
  isExpanded?: boolean;
  /** Called when the chevron/expand area is clicked. */
  onExpandClick?: () => void;
  /** Called when the hamburger (reorder) icon is clicked. Omit to hide the hamburger. */
  onReorderClick?: () => void;
  /** When true, the hamburger icon is the drag handle for drag-and-drop reordering. Requires a dragId (or uses title) for drop handling. */
  draggable?: boolean;
  /** Identifier for this row when draggable; passed in the drag data so the parent can reorder. Defaults to title if omitted. */
  dragId?: string | number;
  /** Called when a drag operation starts on the hamburger. Use with draggable. dataTransfer is already set with dragId. */
  onDragStart?: (e: React.DragEvent) => void;
  /** Called when a drag operation ends (whether or not it was dropped). */
  onDragEnd?: (e: React.DragEvent) => void;
  /** Called when the action menu item is selected (after item.onClick). */
  onActionMenuItemClick?: (item: IconActionMenuItem) => void;
  /** Optional class for the root element. */
  className?: string;
  /** Optional class for the title. */
  titleClassName?: string;
  /** Optional class for the description. */
  descriptionClassName?: string;
  /** Size of the step badge in pixels. Default: 40. Rendered in rem (÷16). */
  badgeSize?: number;
  /** Size of the action (gear) and chevron icons in pixels. Default: 16. Rendered in rem (÷16). */
  actionIconSize?: number;
  /** Portal container for the action menu (e.g. to avoid clipping in tables). */
  getActionMenuPortalContainer?: () => HTMLElement;
  /** Content to show when expanded (chevron opened). Rendered in a full-width div below the header with dynamic height. */
  children?: React.ReactNode;
  /** Optional background color for the expandable area (children). Omit or leave unset to keep the default (no custom background). */
  expandableBgColor?: string;
}

export function SectionHeaderRow({
  stepNumber,
  title,
  description,
  actionMenuItems,
  isExpanded = false,
  onExpandClick,
  onReorderClick,
  onDragStart,
  onDragEnd,
  onActionMenuItemClick,
  className = "",
  titleClassName = "",
  descriptionClassName = "",
  badgeSize = 40,
  actionIconSize = 16,
  getActionMenuPortalContainer,
  children,
  draggable = false,
  dragId,
  expandableBgColor,
}: SectionHeaderRowProps) {
  const showReorderHandle = onReorderClick != null || draggable;

  const handleDragStart = (e: React.DragEvent) => {
    if (!draggable) return;
    const id = dragId !== undefined ? dragId : title;
    e.dataTransfer.setData("application/json", JSON.stringify({ dragId: id }));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(id)); // fallback for some drop targets
    onDragStart?.(e);
  };

  return (
    <div
      className={`section-header-row-wrapper ${className}`.trim()}
      data-name="SectionHeaderRow"
      style={{ width: "100%" }}
    >
      <div
        className="section-header-row"
        style={{
          ["--section-header-row-action-icon-size" as string]: `${actionIconSize / 16}rem`,
        }}
      >
      {showReorderHandle && (
        <button
          type="button"
          className={`section-header-row__reorder ${draggable ? "section-header-row__reorder--draggable" : ""}`.trim()}
          onClick={(e) => {
            e.stopPropagation();
            onReorderClick?.();
          }}
          draggable={draggable}
          onDragStart={handleDragStart}
          onDragEnd={onDragEnd}
          aria-label={draggable ? "Drag to reorder" : "Reorder section"}
        >
          <Menu size={actionIconSize} strokeWidth={2} />
        </button>
      )}

      <div className={`section-header-row__content ${stepNumber == null ? "section-header-row__content--no-badge" : ""}`.trim()}>
        {stepNumber != null && (
          <div
            className="section-header-row__badge"
            style={{ ["--section-header-row-badge-size" as string]: `${badgeSize / 16}rem` }}
            aria-hidden
          >
            {stepNumber}
          </div>
        )}
        <div className="section-header-row__text">
          <p className={`section-header-row__title ${titleClassName}`.trim()}>
            {title}
          </p>
          {description != null && description !== "" && (
            <p className={`section-header-row__description ${descriptionClassName}`.trim()}>
              {description}
            </p>
          )}
        </div>
      </div>

      <div className="section-header-row__actions">
        {onExpandClick != null && (
          <button
            type="button"
            className={`section-header-row__action section-header-row__action--chevron ${isExpanded ? "section-header-row__action--expanded" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onExpandClick();
            }}
            aria-label={isExpanded ? "Collapse section" : "Expand section"}
            aria-expanded={isExpanded}
          >
            <ChevronDown className="section-header-row__chevron" />
          </button>
        )}
        <div className="section-header-row__action section-header-row__action--menu">
          <IconActionMenuTrigger
            items={actionMenuItems}
            onItemClick={onActionMenuItemClick}
            iconSize={actionIconSize}
            getPortalContainer={getActionMenuPortalContainer}
          />
        </div>
      </div>
    </div>

      {isExpanded && children != null ? (
        <div
          className="section-header-row__expandable"
          role="region"
          aria-label="Section content"
          style={
            expandableBgColor != null && expandableBgColor !== ""
              ? { backgroundColor: expandableBgColor }
              : undefined
          }
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
